import { memo, useMemo } from "react"
import { Handle, Position } from "@xyflow/react"
// DomainTooltip hover was interfering with ReactFlow pan/zoom; term details are shown in the right panel instead.

const VARIANTS = {
  actor: { shape: "rectangle", color: "#FFEB3B", textColor: "#000" },
  process: { shape: "rounded", color: "#B39DDB", textColor: "#000" },
  external: { shape: "rectangle", color: "#FFCC80", textColor: "#000" },
}

const BaseNode = memo(({ id, data, selected }) => {
  const variant = VARIANTS[data.variant] || VARIANTS.process
  const hasDrillDown = data.drillDownSlug
  const hasCoverage = data.sources?.expected?.tests?.length > 0
  const gapCount = data.sources?.expected?.gaps?.length || 0

  const glossaryTerms = data.glossaryTerms || []

  const labelEl = useMemo(() => {
    const label = (data.label || "").toString()
    if (glossaryTerms.length === 0 || label.length === 0) return label

    // simple case-insensitive substring replacement, preferring longer terms first
    const candidates = glossaryTerms
      .flatMap((t) => [
        { ...t, match: t.term },
        ...(t.aliases || []).map((a) => ({ ...t, match: a })),
      ])
      .filter((t) => t.match && t.match.trim().length > 0)
      .sort((a, b) => b.match.length - a.match.length)

    const lower = label.toLowerCase()
    const parts = []
    let i = 0

    while (i < label.length) {
      let found = null
      for (const t of candidates) {
        const m = t.match.toLowerCase()
        if (lower.startsWith(m, i)) {
          found = { term: t, matchLen: t.match.length }
          break
        }
      }

      if (!found) {
        parts.push(label[i])
        i += 1
        continue
      }

      const slice = label.slice(i, i + found.matchLen)
      const riskClass =
        found.term.confusion_risk === 2
          ? "risk-high"
          : found.term.confusion_risk === 1
            ? "risk-medium"
            : "risk-low"

      parts.push(
        <span
          key={`${id}-${i}-${found.term.id}`}
          className={`domain-term ${riskClass} nodrag nopan nowheel`}
          title={found.term.term}
        >
          {slice}
        </span>
      )

      i += found.matchLen
    }

    return parts
  }, [data.label, glossaryTerms, id])
  const shapeClass = variant.shape === "rounded" ? "rounded-lg" : ""

  return (
    <div
      className={`base-node relative px-4 py-2 border-2 min-w-[120px] text-center ${shapeClass} ${selected ? "ring-2 ring-blue-500" : ""}`}
      style={{
        backgroundColor: variant.color,
        color: variant.textColor,
        borderColor: selected ? "var(--accent)" : "var(--border-strong)",
      }}
    >
      <Handle type="target" position={Position.Top} style={{ backgroundColor: "var(--border-strong)" }} />

      <div className="node-label font-medium text-sm">{labelEl}</div>
      {data.internalLabel && (
        <div className="node-internal-label text-xs opacity-75 mt-1">{data.internalLabel}</div>
      )}

      <div className="absolute -top-2 -right-2 flex gap-1">
        {hasDrillDown && (
          <span className="drill-indicator bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
            +
          </span>
        )}
        {gapCount > 0 && (
          <span
            className="gap-badge bg-amber-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-[11px] font-bold"
            title={`${gapCount} gap${gapCount === 1 ? "" : "s"}`}
          >
            !
          </span>
        )}
        {hasCoverage && (
          <span className="coverage-badge bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
            ✓
          </span>
        )}
      </div>

      <Handle type="source" position={Position.Bottom} style={{ backgroundColor: "var(--border-strong)" }} />
    </div>
  )
})

BaseNode.displayName = "BaseNode"

export default BaseNode
